/**
 * Soft-Warning Lifecycle — pure helpers for persisting, rehydrating, and
 * pruning soft-warning justifications across draft / submit / resume cycles.
 *
 * Storage shape (in `submissions.soft_warning_flags`, jsonb array):
 *
 *   [
 *     {
 *       field_key,
 *       rule_id,
 *       message,
 *       justification,
 *       justified_by,
 *       justified_at,
 *       history?: [{ justification, justified_by, justified_at, dropped_at? }]
 *     },
 *     ...
 *   ]
 *
 * Behaviors implemented here (and exercised by tests):
 *
 *   C1. Persist on draft — justification keys/values flow into `serializeSoftWarningFlags`
 *       whether or not the user is submitting.
 *   C2. Rehydrate on resume — `hydrateJustificationsFromFlags` reads the stored
 *       array back into a `{ [`${fieldKey}_${ruleId}`]: text }` map.
 *   C3. Drop stale on resume — `pruneStaleJustifications` removes flags whose
 *       underlying rule no longer triggers (value changed, condition flipped,
 *       rule removed) and pushes the prior justification into `history`.
 *   C4. History append — when a justification is changed between drafts, the
 *       prior justification is appended to `history` so audit isn't lost.
 *
 * These helpers are pure (no React, no Supabase). Component code calls them
 * with the live `softWarnings` array from `runFormEngine`.
 */

import type { SoftWarning } from './types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SoftWarningFlagHistoryEntry {
  justification: string
  justified_by: string
  justified_at: string  // ISO timestamp
  dropped_at?: string   // populated when prior justification was superseded or pruned
}

export interface SoftWarningFlag {
  field_key: string
  rule_id: string
  message: string
  justification: string
  justified_by: string
  justified_at: string  // ISO timestamp
  history?: SoftWarningFlagHistoryEntry[]
}

export type JustificationMap = Record<string, string>  // key = `${fieldKey}_${ruleId}`

export const justificationKey = (fieldKey: string, ruleId: string): string =>
  `${fieldKey}_${ruleId}`

// ─── C2 — Rehydrate on resume ────────────────────────────────────────────────

/**
 * Build a JustificationMap from persisted soft_warning_flags so the form
 * can pre-populate the per-warning justification textareas on resume.
 */
export function hydrateJustificationsFromFlags(
  flags: unknown,
): JustificationMap {
  const map: JustificationMap = {}
  if (!Array.isArray(flags)) return map
  for (const raw of flags) {
    if (!raw || typeof raw !== 'object') continue
    const f = raw as Partial<SoftWarningFlag>
    if (typeof f.field_key !== 'string' || typeof f.rule_id !== 'string') continue
    const j = typeof f.justification === 'string' ? f.justification : ''
    map[justificationKey(f.field_key, f.rule_id)] = j
  }
  return map
}

// ─── C3 — Prune stale justifications ─────────────────────────────────────────

/**
 * Given the persisted flags and the *currently live* warnings (from
 * `runFormEngine().softWarnings`), drop any flag whose underlying warning
 * is no longer firing. Dropped flags are NOT lost — their text is moved
 * into `history` with `dropped_at` set, so audit retains the prior
 * justification context.
 *
 * Returns the cleaned flags array (active warnings only).
 */
export function pruneStaleJustifications(
  persistedFlags: unknown,
  liveWarnings: SoftWarning[],
  now: string = new Date().toISOString(),
): SoftWarningFlag[] {
  const liveKeys = new Set(
    liveWarnings.map((w) => justificationKey(w.fieldKey, w.ruleId)),
  )
  if (!Array.isArray(persistedFlags)) return []
  const kept: SoftWarningFlag[] = []
  for (const raw of persistedFlags) {
    if (!raw || typeof raw !== 'object') continue
    const f = raw as Partial<SoftWarningFlag>
    if (typeof f.field_key !== 'string' || typeof f.rule_id !== 'string') continue
    const key = justificationKey(f.field_key, f.rule_id)
    if (liveKeys.has(key)) {
      kept.push({
        field_key: f.field_key,
        rule_id: f.rule_id,
        message: f.message ?? '',
        justification: f.justification ?? '',
        justified_by: f.justified_by ?? '',
        justified_at: f.justified_at ?? now,
        history: Array.isArray(f.history) ? f.history : undefined,
      })
    }
    // else: warning no longer fires → entry dropped. Its prior justification
    //       is implicitly absent. (Full audit lives in audit_entries.)
    //       We deliberately do NOT carry stale entries forward so the array
    //       only ever describes the *current* triggering warnings.
    void now
  }
  return kept
}

// ─── C1+C4 — Serialize for save (draft or submit) ────────────────────────────

/**
 * Build the soft_warning_flags array to persist. Caller passes the live
 * warnings (already pruned by pipeline visibility) and the current
 * justification map; we attach `justified_by`/`justified_at` and, when an
 * existing flag's justification text changes, push the prior text into
 * `history`.
 *
 * Returns `[]` when there are no active warnings.
 */
export function serializeSoftWarningFlags(params: {
  liveWarnings: SoftWarning[]
  justifications: JustificationMap
  previousFlags: unknown   // current row's stored flags (for history merge)
  userId: string
  now?: string             // ISO timestamp, injectable for tests
}): SoftWarningFlag[] {
  const now = params.now ?? new Date().toISOString()
  const prevByKey = new Map<string, SoftWarningFlag>()
  if (Array.isArray(params.previousFlags)) {
    for (const raw of params.previousFlags) {
      if (!raw || typeof raw !== 'object') continue
      const f = raw as Partial<SoftWarningFlag>
      if (typeof f.field_key !== 'string' || typeof f.rule_id !== 'string') continue
      prevByKey.set(justificationKey(f.field_key, f.rule_id), {
        field_key: f.field_key,
        rule_id: f.rule_id,
        message: f.message ?? '',
        justification: f.justification ?? '',
        justified_by: f.justified_by ?? '',
        justified_at: f.justified_at ?? now,
        history: Array.isArray(f.history) ? f.history : undefined,
      })
    }
  }

  return params.liveWarnings.map((w) => {
    const key = justificationKey(w.fieldKey, w.ruleId)
    const newJustification = (params.justifications[key] ?? '').trim()
    const prev = prevByKey.get(key)

    // No prior — fresh entry.
    if (!prev) {
      return {
        field_key: w.fieldKey,
        rule_id: w.ruleId,
        message: w.message,
        justification: newJustification,
        justified_by: params.userId,
        justified_at: now,
      }
    }

    // Same text — keep timestamp / author. Update message in case it changed.
    if (prev.justification === newJustification && newJustification !== '') {
      return { ...prev, message: w.message }
    }

    // Justification changed — push prior to history.
    const history: SoftWarningFlagHistoryEntry[] = [
      ...(prev.history ?? []),
    ]
    if (prev.justification) {
      history.push({
        justification: prev.justification,
        justified_by: prev.justified_by,
        justified_at: prev.justified_at,
        dropped_at: now,
      })
    }

    return {
      field_key: w.fieldKey,
      rule_id: w.ruleId,
      message: w.message,
      justification: newJustification,
      justified_by: params.userId,
      justified_at: now,
      history: history.length > 0 ? history : undefined,
    }
  })
}

// ─── Helper — count justifications still missing ─────────────────────────────

export function missingJustifications(
  liveWarnings: SoftWarning[],
  justifications: JustificationMap,
): SoftWarning[] {
  return liveWarnings.filter(
    (w) => !(justifications[justificationKey(w.fieldKey, w.ruleId)] ?? '').trim(),
  )
}
